import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Trash2, AlertTriangle, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeleteAccount() {
  const { user, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');

  const handleDeleteRequest = async () => {
    if (!user) {
      toast.error('Please log in to delete your account');
      return;
    }

    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/users/delete-account`, {
        password,
        reason,
      });
      
      toast.success('Account deletion request submitted');
      setStep(3);
      
      // Log out after successful deletion request
      setTimeout(() => {
        logout();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process deletion request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-dark-surface/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to vgoride</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center">
              <span className="font-bold text-sm">V</span>
            </div>
            <span className="font-outfit font-bold text-lg">vgoride</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Page Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl mb-2">Delete Your Account</h1>
          <p className="text-white/60">Permanently remove your vgoride account and data</p>
        </div>

        {/* Step 1: Information */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-6">
                <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-bold text-lg mb-2">Before you continue</h2>
                  <p className="text-white/70 text-sm">
                    Deleting your account is permanent and cannot be undone. Please read the following carefully:
                  </p>
                </div>
              </div>

              <ul className="space-y-3 text-sm text-white/70 ml-10">
                <li className="flex items-start gap-2">
                  <span className="text-red-400">•</span>
                  <span>All your personal data will be permanently deleted</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">•</span>
                  <span>Your ride history and saved locations will be removed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">•</span>
                  <span>Any pending rides will be cancelled</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">•</span>
                  <span>Driver earnings history will be deleted (ensure you&apos;ve withdrawn funds)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">•</span>
                  <span>You won&apos;t be able to recover your account or data</span>
                </li>
              </ul>
            </div>

            {user ? (
              <button
                onClick={() => setStep(2)}
                className="w-full bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-medium py-4 transition-colors"
                data-testid="continue-delete-btn"
              >
                I understand, continue
              </button>
            ) : (
              <div className="glass-panel rounded-2xl p-6 text-center">
                <p className="text-white/70 mb-4">Please log in to delete your account</p>
                <Link
                  to="/auth"
                  className="inline-block bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-xl font-bold px-6 py-3 transition-colors"
                >
                  Log In
                </Link>
              </div>
            )}

            <div className="text-center">
              <p className="text-white/50 text-sm mb-2">Need help or have questions?</p>
              <a 
                href="mailto:delete-account@vgoride.net" 
                className="inline-flex items-center gap-2 text-neon-cyan hover:underline"
              >
                <Mail className="w-4 h-4" />
                delete-account@vgoride.net
              </a>
            </div>
          </div>
        )}

        {/* Step 2: Confirmation */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6">
              <h2 className="font-bold text-lg mb-4">Confirm Account Deletion</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/70 mb-2">
                    Why are you leaving? (Optional)
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full bg-dark-subtle border border-white/10 rounded-xl p-3 text-white"
                    data-testid="delete-reason-select"
                  >
                    <option value="">Select a reason</option>
                    <option value="no_longer_needed">I no longer need this service</option>
                    <option value="privacy_concerns">Privacy concerns</option>
                    <option value="too_many_emails">Too many emails/notifications</option>
                    <option value="found_alternative">Found an alternative service</option>
                    <option value="poor_experience">Poor experience with the app</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">
                    Enter your password to confirm
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className="w-full bg-dark-subtle border border-white/10 focus:border-red-400 rounded-xl p-3 text-white placeholder:text-white/40"
                    data-testid="delete-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">
                    Type <span className="text-red-400 font-mono">DELETE</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                    placeholder="Type DELETE"
                    className="w-full bg-dark-subtle border border-white/10 focus:border-red-400 rounded-xl p-3 text-white placeholder:text-white/40 font-mono"
                    data-testid="delete-confirm-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-white/10 hover:bg-white/20 rounded-xl font-medium py-4 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleDeleteRequest}
                disabled={loading || confirmText !== 'DELETE' || !password}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-bold py-4 transition-colors"
                data-testid="confirm-delete-btn"
              >
                {loading ? 'Processing...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="glass-panel rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="font-bold text-xl mb-2">Request Submitted</h2>
            <p className="text-white/70 mb-6">
              Your account deletion request has been submitted. Your account and all associated data will be permanently deleted within 24-48 hours.
            </p>
            <p className="text-white/50 text-sm">
              You will be logged out automatically. Thank you for using vgoride.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 text-center text-white/50 text-sm">
          <p>© {new Date().getFullYear()} vgoride. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span>•</span>
            <a href="mailto:customer-support@vgoride.net" className="hover:text-white transition-colors">Contact Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
